import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Button, Checkbox, List, Menu, Modal, Portal, RadioButton, Switch, Text, TextInput } from 'react-native-paper';
import { HAMBURGER_ICONS } from '../../../constants/hamburgerIcons';
import { colors, spacing, typography } from '../../../constants/theme';
import { ItemForm, ReviewCategory, ReviewItem } from '../AdminDashboard.types';

interface AdminEditItemModalProps {
  visible: boolean;
  item: ReviewItem | null;
  form: ItemForm;
  categories: ReviewCategory[];
  categoryMenuVisible: boolean;
  selectedTemplateId: number | null;
  onChangeForm: (form: ItemForm) => void;
  onChangeCategoryMenuVisible: (visible: boolean) => void;
  onDismiss: () => void;
  onEditQuestions: () => void;
  onSubmit: () => void;
}

export const AdminEditItemModal: React.FC<AdminEditItemModalProps> = ({
  visible,
  item,
  form,
  categories,
  categoryMenuVisible,
  selectedTemplateId,
  onChangeForm,
  onChangeCategoryMenuVisible,
  onDismiss,
  onEditQuestions,
  onSubmit,
}) => {
  if (!item) {
    return null;
  }

  const parentCategory = categories.find(category => category.id.toString() === form.categoryId);
  const categoryInHamburger = parentCategory?.showInHamburger || false;

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={styles.modal}
      >
        <ScrollView testID="edit-item-modal">
          <Text style={styles.modalTitle}>Edit Item: {item.name}</Text>

          <View style={styles.formContainer}>
            <TextInput
              label="Name *"
              value={form.name}
              onChangeText={(text) => onChangeForm({ ...form, name: text })}
              style={styles.formField}
            />

            <TextInput
              label="Description"
              value={form.description}
              onChangeText={(text) => onChangeForm({ ...form, description: text })}
              multiline
              style={styles.formField}
            />

            <Text>Category *</Text>
            <Menu
              visible={categoryMenuVisible}
              onDismiss={() => onChangeCategoryMenuVisible(false)}
              anchor={
                <Button
                  mode="outlined"
                  onPress={() => onChangeCategoryMenuVisible(true)}
                  style={styles.formField}
                >
                  {form.categoryId
                    ? categories.find(category => category.id.toString() === form.categoryId)?.name || 'Select Category'
                    : 'Select Category'
                  }
                </Button>
              }
            >
              {categories.filter(category => category.templateId === selectedTemplateId).map(category => (
                <Menu.Item
                  key={category.id}
                  title={category.name}
                  onPress={() => {
                    onChangeForm({ ...form, categoryId: category.id.toString() });
                    onChangeCategoryMenuVisible(false);
                  }}
                />
              ))}
            </Menu>

            <View style={styles.checkboxSection}>
              <Text style={styles.checkboxSectionTitle}>Field Requirements</Text>

              <View style={styles.checkboxRow}>
                <Checkbox
                  status={form.requiresLocation ? 'checked' : 'unchecked'}
                  onPress={() => onChangeForm({
                    ...form,
                    requiresLocation: !form.requiresLocation,
                  })}
                />
                <Text style={styles.checkboxLabel}>Requires Location/Room</Text>
              </View>

              <View style={styles.checkboxRow}>
                <Checkbox
                  status={form.requiresFloor ? 'checked' : 'unchecked'}
                  onPress={() => onChangeForm({
                    ...form,
                    requiresFloor: !form.requiresFloor,
                  })}
                />
                <Text style={styles.checkboxLabel}>Requires Floor</Text>
              </View>
            </View>

            <View style={styles.hamburgerSettings}>
              {categoryInHamburger && (
                <Text style={styles.hamburgerHelperText}>
                  ℹ️ In hamburger via category - select item icon below
                </Text>
              )}
              <View style={styles.switchRow}>
                <Switch
                  value={form.showInHamburger}
                  onValueChange={(value) => onChangeForm({ ...form, showInHamburger: value })}
                />
                <Text style={styles.switchLabel}>
                  Show in Hamburger Menu
                </Text>
              </View>
              {(form.showInHamburger || categoryInHamburger) && (
                <>
                  <Text style={styles.iconLabel}>Hamburger Icon</Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                    <View style={styles.iconGrid}>
                      {HAMBURGER_ICONS.map(iconOption => (
                        <Button
                          key={iconOption.value}
                          mode={form.hamburgerIcon === iconOption.value ? 'contained' : 'outlined'}
                          onPress={() => onChangeForm({ ...form, hamburgerIcon: iconOption.value })}
                          style={styles.iconButton}
                        >
                          {iconOption.label}
                        </Button>
                      ))}
                    </View>
                  </ScrollView>
                  <TextInput
                    label="Display Order (lower appears first)"
                    value={form.hamburgerOrder?.toString() || '0'}
                    onChangeText={(text) => {
                      const num = parseInt(text, 10) || 0;
                      onChangeForm({ ...form, hamburgerOrder: Math.max(0, num) });
                    }}
                    keyboardType="numeric"
                    style={styles.displayOrderField}
                  />
                </>
              )}
            </View>

            <Button
              mode="outlined"
              onPress={onEditQuestions}
              style={[styles.formField, styles.questionsButton]}
            >
              Edit Questions
            </Button>

            <View style={styles.findingSettings}>
              <Text style={styles.checkboxSectionTitle}>Finding classification</Text>
              <Text style={styles.findingHelperText}>
                Findings are optional. Choose whether reviewers may flag the entire item or selected questions.
              </Text>
              <RadioButton.Group
                value={form.findingMode}
                onValueChange={(mode) => onChangeForm({
                  ...form,
                  findingMode: mode as ItemForm['findingMode'],
                  findingQuestionIds: mode === 'questions' ? form.findingQuestionIds : [],
                })}
              >
                {([
                  ['off', 'Off'],
                  ['item', 'Entire item'],
                  ['questions', 'Selected questions'],
                ] as const).map(([mode, label]) => (
                  <List.Item
                    key={mode}
                    testID={`finding-mode-${mode}`}
                    title={label}
                    left={() => <RadioButton value={mode} />}
                    onPress={() => onChangeForm({
                      ...form,
                      findingMode: mode,
                      findingQuestionIds: mode === 'questions' ? form.findingQuestionIds : [],
                    })}
                  />
                ))}
              </RadioButton.Group>

              {form.findingMode === 'questions' && (
                <View testID="finding-question-selector">
                  {(item.questionsSchema?.fields || [])
                    .filter(question => !question.deprecated)
                    .map(question => {
                      const selected = form.findingQuestionIds.includes(question.id);
                      return (
                        <View key={question.id} style={styles.checkboxRow}>
                          <Checkbox
                            testID={`finding-question-${question.id}`}
                            status={selected ? 'checked' : 'unchecked'}
                            onPress={() => onChangeForm({
                              ...form,
                              findingQuestionIds: selected
                                ? form.findingQuestionIds.filter(id => id !== question.id)
                                : [...form.findingQuestionIds, question.id],
                            })}
                          />
                          <Text style={styles.checkboxLabel}>{question.label || question.id}</Text>
                        </View>
                      );
                    })}
                  {(item.questionsSchema?.fields || []).filter(question => !question.deprecated).length === 0 && (
                    <Text style={styles.findingHelperText}>Add questions before selecting question-level findings.</Text>
                  )}
                </View>
              )}
            </View>
          </View>

          <View style={styles.formRow}>
            <Button
              testID="edit-item-cancel-button"
              mode="outlined"
              onPress={onDismiss}
              style={styles.formField}
            >
              Cancel
            </Button>
            <Button
              testID="edit-item-update-button"
              mode="contained"
              onPress={onSubmit}
              style={styles.formField}
            >
              Update
            </Button>
          </View>
        </ScrollView>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modal: {
    backgroundColor: colors.background,
    margin: spacing.lg,
    padding: spacing.lg,
    borderRadius: 8,
    maxHeight: '90%',
    minHeight: '70%',
  },
  modalTitle: {
    ...typography.h2,
    marginBottom: spacing.lg,
  },
  formContainer: {
    gap: spacing.md,
  },
  formField: {
    flex: 1,
    marginBottom: spacing.md,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
    paddingHorizontal: spacing.sm,
  },
  checkboxSection: {
    marginVertical: spacing.md,
    paddingVertical: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  checkboxSectionTitle: {
    ...typography.bodyBold,
    marginBottom: spacing.sm,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: spacing.xs,
  },
  checkboxLabel: {
    ...typography.body,
    marginLeft: spacing.sm,
  },
  hamburgerSettings: {
    marginTop: spacing.md,
  },
  hamburgerHelperText: {
    color: colors.primary,
    fontSize: 12,
    marginBottom: spacing.sm,
  },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  switchLabel: {
    marginLeft: spacing.sm,
  },
  iconLabel: {
    marginBottom: spacing.xs,
  },
  iconGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  iconButton: {
    minWidth: 100,
    marginBottom: spacing.xs,
  },
  displayOrderField: {
    marginTop: spacing.sm,
  },
  questionsButton: {
    borderColor: colors.primary,
    marginTop: spacing.md,
    marginBottom: spacing.lg,
  },
  findingSettings: {
    marginBottom: spacing.lg,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  findingHelperText: {
    ...typography.caption,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
  },
  formRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
});

export default AdminEditItemModal;
